import { isLocalAudio, type SentenceAudioBlock } from "./audioBlockMap";
import { audioPreparationKey } from "./audioPreparation";
import { audioSourceBlockIds } from './cachedAudioBlocks';

export type AudioState = "syncing" | "needs_voice" | "queued" | "generating" | "saving" | "ready" | "failed" | "repair_required" | "unknown";
export interface AudioArtifactStatus {
  input_hash: string; state: AudioState; verified: boolean; updated_at?: string;
  error_code?: string | null; retry_at?: string;
}
export interface SentenceAudioStatus {
  id: string; blockId: string; text: string; inputHash?: string; state: AudioState;
  verified: boolean; updatedAt?: string; errorCode?: string | null; retryAt?: string;
  local?: boolean;
  blockIds?: string[];
}
export const AUDIO_STATE_LABEL: Record<AudioState, string> = {
  syncing: "Syncing changes", needs_voice: "Needs a voice", queued: "Queued", generating: "Generating",
  saving: "Saving audio", ready: "Ready", failed: "Failed", repair_required: "Needs recovery", unknown: "Status unavailable",
};
/** Never reuse a prior revision's readiness. Duplicate sentences can share one
 * artifact while retaining a lookup entry for every occurrence in each block. */
export function sentenceAudioStates(sentences: SentenceAudioBlock[], revisions: ReadonlyMap<string, string>,
  hashes?: ReadonlyMap<string, string>, artifacts?: AudioArtifactStatus[], disconnected = false): SentenceAudioStatus[] {
  const byHash = new Map((artifacts ?? []).map(a => [a.input_hash, a]));
  return sentences.map(sentence => {
    if (isLocalAudio(sentence)) return { id: sentence.id, blockId: sentence.sourceBlockId,
      text: [sentence.sourceText ? `Not spoken: ${sentence.sourceText}` : "", sentence.silenceMs ? `Pause ${sentence.silenceMs / 1000}s` : ""].filter(Boolean).join(" · "),
      state: "ready", verified: true, local: true };
    const revision = revisions.get(sentence.voiceId);
    const hash = revision ? hashes?.get(audioPreparationKey(sentence.voiceId, revision, sentence.text, sentence.synthesisOptions)) : undefined;
    const artifact = hash ? byHash.get(hash) : undefined;
    return { id: sentence.id, blockId: sentence.sourceBlockId, blockIds: audioSourceBlockIds(sentence), text: sentence.text, inputHash: hash,
      state: !revision ? "needs_voice" : disconnected ? "unknown" : artifact?.state ?? "syncing",
      verified: !disconnected && artifact?.verified === true, updatedAt: artifact?.updated_at,
      errorCode: artifact?.error_code, retryAt: artifact?.retry_at };
  });
}
